import { useState } from "react";
import type { RecipeEvidence } from "../lib/api";

/**
 * The reliability strip — what makes this a buy list you can trust rather than one
 * more confident-looking screen.
 *
 * A ranked list of tickers is equally persuasive whether the rule behind it has a
 * real edge, no edge, or a negative one. This strip is the antidote: it refuses to let
 * the names be read without their record. It is COMPACT by default so the list stays
 * visible, but nothing load-bearing is hidden — the two metrics, the money, and the
 * luck verdict are always on screen; only the prose (what the edge actually is, the
 * measurement caveats) folds behind "why ▾".
 *
 * Non-negotiable even when compressed:
 *  - BOTH metrics: IC-t (orders stocks) and excess-t (beats the market) disagree often
 *    enough that either alone misleads — the best ranker measured LOSES to the
 *    benchmark. Never show one without the other.
 *  - The luck verdict in words: a t of +1.04 next to a bar of 2.23 only means something
 *    if the reader is told the bar is there.
 */
export function ReliabilityStrip({ e, description }: { e: RecipeEvidence; description: string }) {
  const [open, setOpen] = useState(false);
  const cleared = e.beats_luck_benchmark;
  return (
    <div className="border-b border-term-edge bg-term-bg/60 px-2 py-1.5 space-y-1.5 text-[11px]">
      {/* The two metrics + the selection/allocation verdict, one tight row. */}
      <div className="flex items-stretch gap-2">
        <Metric label="ranks" value={fmtT(e.ic_t)} sub="IC t" good={e.ic_t >= 2} />
        <Metric label="pays" value={fmtT(e.excess_t)} sub="excess t" good={cleared} />
        <span
          className={`flex items-center rounded px-1.5 text-[10px] uppercase tracking-wide ${
            e.survives_neutralization
              ? "bg-term-accent/20 text-term-accent"
              : "bg-term-edge text-term-dim"
          }`}
        >
          {e.survives_neutralization ? "selection" : "allocation"}
        </span>
      </div>

      {/* The money, one line. */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 tnum text-term-dim">
        <span>
          <span className={e.excess_annual > 0 ? "text-term-up" : "text-term-down"}>
            {fmtPct(e.cagr)}
          </span>
          /yr vs {fmtPct(e.bench_cagr)}
        </span>
        <span>DD {fmtPct(e.max_drawdown)}</span>
        <span>turn {fmtPct(e.turnover)}</span>
        <span>n={e.n_periods}</span>
      </div>

      {/* The luck verdict — always visible, it is the whole point. */}
      <div
        className={`rounded px-1.5 py-0.5 leading-snug ${
          cleared ? "bg-term-up/10 text-term-up" : "bg-term-down/10 text-term-down"
        }`}
      >
        t={fmtT(e.excess_t)} {cleared ? "clears" : "below"} the luck bar {e.luck_benchmark_t.toFixed(2)}{" "}
        ({e.configurations_tried} tried){!cleared && " — reasonable portfolio, not a proven edge"}
      </div>

      {/* Everything verbose folds away by default. */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-term-dim hover:text-term-text text-[10px] uppercase tracking-wide"
      >
        {open ? "less ▴" : "why ▾"}
      </button>
      {open && (
        <div className="space-y-1.5 pt-0.5 text-term-dim leading-relaxed">
          <p>{description}</p>
          <p>{e.what_it_actually_is}</p>
          <p className="text-[10px]">
            Measured {e.window}. Point-in-time, survivorship-free, investable universe only.
          </p>
        </div>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  sub,
  good,
}: {
  label: string;
  value: string;
  sub: string;
  good: boolean;
}) {
  return (
    <div className="flex-1 rounded border border-term-edge px-1.5 py-0.5 leading-tight">
      <div className="text-[10px] uppercase tracking-wide text-term-dim">{label}</div>
      <span className={`tnum text-base ${good ? "text-term-up" : "text-term-text"}`}>{value}</span>
      <span className="text-[10px] text-term-dim ml-1">{sub}</span>
    </div>
  );
}

function fmtT(t: number): string {
  return (t >= 0 ? "+" : "") + t.toFixed(2);
}

function fmtPct(x: number): string {
  return (x >= 0 ? "+" : "") + (x * 100).toFixed(1) + "%";
}
